/**
 * Swatch selects
 *
 * Turns any select tagged with data-swatch-select into a listbox that shows a
 * square colour or image chip beside every label, which a native select cannot
 * render.
 *
 * The select itself stays in the DOM and keeps ownership of the value, so the
 * theme's variant engine and the per-unit cart splitter carry on reading it
 * untouched, and a JS failure leaves a working dropdown behind.
 */
(function () {
  'use strict';

  var SELECT = '[data-swatch-select]';
  var READY = 'data-swatch-select-ready';
  var OPTION = '[data-swatch-option]';
  var REFRESH_EVENT = 'swatch-select:refresh';
  var VARIANT_EVENT = 'onVariantChange';
  var instances = [];
  var uid = 0;

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function element(tag, className) {
    var node = document.createElement(tag);

    if (className) node.className = className;

    return node;
  }

  function chip(style) {
    var node = element('span', 'c-swatch-select__chip');

    if (style) {
      node.setAttribute('style', style);
    } else {
      node.classList.add('is-empty');
    }

    return node;
  }

  function SwatchSelect(select) {
    this.select = select;
    this.options = toArray(select.options);
    this.id = 'swatch-select-' + (uid += 1);
    this.isOpen = false;
    this.activeIndex = Math.max(select.selectedIndex, 0);

    this.build();
    this.bind();
    this.sync();
  }

  SwatchSelect.prototype.build = function () {
    var label = this.select.getAttribute('aria-label');

    this.wrapper = element('div', 'c-swatch-select');
    this.select.parentNode.insertBefore(this.wrapper, this.select);
    this.wrapper.appendChild(this.select);
    this.select.classList.add('c-swatch-select__native');

    this.trigger = element('button', 'c-swatch-select__trigger');
    this.trigger.type = 'button';
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', this.id);
    if (label) this.trigger.setAttribute('aria-label', label);

    this.triggerChip = chip('');
    this.triggerLabel = element('span', 'c-swatch-select__label');

    this.trigger.appendChild(this.triggerChip);
    this.trigger.appendChild(this.triggerLabel);
    this.trigger.appendChild(element('span', 'c-swatch-select__caret'));

    this.list = element('div', 'c-swatch-select__list');
    this.list.id = this.id;
    this.list.tabIndex = -1;
    this.list.setAttribute('role', 'listbox');
    if (label) this.list.setAttribute('aria-label', label);
    this.list.hidden = true;

    this.items = this.options.map(function (option, index) {
      var item = element('div', 'c-swatch-select__option');
      var text = element('span', 'c-swatch-select__label');

      item.id = this.id + '-option-' + index;
      item.setAttribute('role', 'option');
      item.setAttribute('data-swatch-option', '');
      item.setAttribute('data-index', String(index));

      text.textContent = option.textContent.trim();

      item.appendChild(chip(option.getAttribute('data-swatch-style')));
      item.appendChild(text);
      this.list.appendChild(item);

      return item;
    }, this);

    this.wrapper.appendChild(this.trigger);
    this.wrapper.appendChild(this.list);
    this.select.setAttribute(READY, '');
  };

  SwatchSelect.prototype.bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function () {
      if (self.isOpen) {
        self.close();
      } else {
        self.open();
      }
    });

    this.trigger.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        self.open();
      }
    });

    // Keeps focus on the list so the option is still rendered when click fires.
    this.list.addEventListener('mousedown', function (event) {
      event.preventDefault();
    });

    this.list.addEventListener('click', function (event) {
      var item = event.target.closest ? event.target.closest(OPTION) : null;

      if (item) self.commit(parseInt(item.getAttribute('data-index'), 10));
    });

    this.list.addEventListener('keydown', function (event) {
      self.onKeydown(event);
    });

    this.list.addEventListener('focusout', function (event) {
      if (!self.wrapper.contains(event.relatedTarget)) self.close(false);
    });

    // The engine and the per-unit script both write to the select directly.
    this.select.addEventListener('change', function () {
      self.sync();
    });

    this.select.addEventListener(REFRESH_EVENT, function () {
      self.sync();
    });
  };

  SwatchSelect.prototype.onKeydown = function (event) {
    var key = event.key;

    // Tab hands focus back to the trigger first so tabbing carries on in order.
    if (key === 'Escape' || key === 'Tab') {
      this.close();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.commit(this.activeIndex);
      return;
    }

    var step = 0;

    if (key === 'ArrowDown') step = 1;
    if (key === 'ArrowUp') step = -1;

    if (step) {
      event.preventDefault();
      this.moveActive(this.activeIndex + step, step);
      return;
    }

    if (key === 'Home') {
      event.preventDefault();
      this.moveActive(0, 1);
    }

    if (key === 'End') {
      event.preventDefault();
      this.moveActive(this.options.length - 1, -1);
    }
  };

  SwatchSelect.prototype.moveActive = function (index, step) {
    var next = index;

    while (next >= 0 && next < this.options.length) {
      if (!this.options[next].disabled) {
        this.setActive(next);
        return;
      }

      next += step;
    }
  };

  SwatchSelect.prototype.setActive = function (index) {
    this.activeIndex = index;
    this.list.setAttribute('aria-activedescendant', this.items[index].id);

    this.items.forEach(function (item, position) {
      item.classList.toggle('is-active', position === index);
    });

    if (this.items[index].scrollIntoView) {
      this.items[index].scrollIntoView({ block: 'nearest' });
    }
  };

  SwatchSelect.prototype.open = function () {
    if (this.isOpen) return;

    this.isOpen = true;
    this.list.hidden = false;
    this.wrapper.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');

    var selected = Math.max(this.select.selectedIndex, 0);

    this.moveActive(selected, 1);
    this.list.focus();
  };

  SwatchSelect.prototype.close = function (returnFocus) {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.list.hidden = true;
    this.wrapper.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');

    if (returnFocus !== false) this.trigger.focus();
  };

  SwatchSelect.prototype.commit = function (index) {
    var option = this.options[index];

    if (!option) return;

    // Keeps the keyboard alive after a click that cannot be honoured.
    if (option.disabled) {
      this.list.focus();
      return;
    }

    if (this.select.selectedIndex !== index) {
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();
  };

  SwatchSelect.prototype.sync = function () {
    var index = Math.max(this.select.selectedIndex, 0);
    var option = this.options[index];

    if (!option) return;

    this.triggerLabel.textContent = option.textContent.trim();
    this.triggerChip.setAttribute('style', option.getAttribute('data-swatch-style') || '');
    this.triggerChip.classList.toggle('is-empty', !option.getAttribute('data-swatch-style'));

    this.items.forEach(function (item, position) {
      var itemOption = this.options[position];

      item.setAttribute('aria-selected', position === index ? 'true' : 'false');
      item.setAttribute('aria-disabled', itemOption.disabled ? 'true' : 'false');
      item.classList.toggle('is-selected', position === index);
      item.classList.toggle('is-disabled', itemOption.disabled);
    }, this);
  };

  function closeAll(event) {
    instances.forEach(function (instance) {
      if (!instance.wrapper.contains(event.target)) instance.close(false);
    });
  }

  // Escape is also handled here so it works even when something outside the
  // list holds focus, which assistive tech and overlays can both cause.
  function closeOnEscape(event) {
    if (event.key !== 'Escape') return;

    instances.forEach(function (instance) {
      if (instance.isOpen) instance.close();
    });
  }

  function syncAll() {
    instances.forEach(function (instance) {
      instance.sync();
    });
  }

  /**
   * A dropdown where nothing has a swatch yet would only gain a column of
   * placeholder squares, so it is left as the native select until the first
   * value has either a swatch entry or a variant image.
   */
  function hasSwatches(select) {
    return toArray(select.options).some(function (option) {
      return !!option.getAttribute('data-swatch-style');
    });
  }

  function init(scope) {
    var root = scope || document;

    toArray(root.querySelectorAll(SELECT)).forEach(function (select) {
      if (select.hasAttribute(READY) || !select.options.length) return;
      if (!hasSwatches(select)) return;

      instances.push(new SwatchSelect(select));
    });
  }

  document.addEventListener('mousedown', closeAll);
  document.addEventListener('keydown', closeOnEscape);
  // Safety net for any silent value write the engine makes on variant change.
  document.addEventListener(VARIANT_EVENT, syncAll);

  window.mpSwatchSelectInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
